import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
import { useNavigate, useParams } from 'react-router-dom';
import { api, Prediction, Quote, CompanyProfile } from '../api';
import { useAuth } from '../AuthContext';

const TIMEFRAMES = ['daily', 'weekly', 'monthly', 'yearly'] as const;
type Timeframe = typeof TIMEFRAMES[number];

const STOCK_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4'];

/** Format market cap (Finnhub returns it in millions of USD). */
function formatMarketCap(millions?: number | null): string {
  if (millions == null || !isFinite(millions) || millions <= 0) return 'N/A';
  if (millions >= 1_000_000) return `$${(millions / 1_000_000).toFixed(2)}T`;
  if (millions >= 1_000) return `$${(millions / 1_000).toFixed(2)}B`;
  return `$${millions.toFixed(2)}M`;
}

interface StockSnapshot {
  symbol: string;
  currentPrice: number | null;
  marketCap: number | null;
  predictions: Record<Timeframe, Prediction | null>;
}

export default function Compare() {
  const { symbols: urlSymbols } = useParams<{ symbols?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [allSymbols, setAllSymbols] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [timeframe, setTimeframe] = useState<Timeframe>('daily');
  const [snapshots, setSnapshots] = useState<StockSnapshot[]>([]);
  const [loading, setLoading] = useState(false);

  // Load available symbols
  useEffect(() => {
    const s = user?.stocks || [];
    setAllSymbols(s);
    if (urlSymbols) {
      const parsed = urlSymbols.split(',').map((v) => v.trim().toUpperCase()).filter((v) => s.includes(v));
      if (parsed.length >= 2) {
        setSelected(parsed.slice(0, 5));
        return;
      }
    }
    if (s.length >= 2) setSelected(s.slice(0, 3));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.stocks]);

  // Update URL
  useEffect(() => {
    if (selected.length >= 2) {
      navigate(`/compare/${selected.join(',')}`, { replace: true });
    }
  }, [selected, navigate]);

  // Fetch data for all selected stocks
  useEffect(() => {
    if (selected.length < 2) return;
    setLoading(true);

    const promises = selected.map(async (sym) => {
      const [preds, quotes, profile] = await Promise.all([
        api.getPredictions(sym),
        api.getQuotes(sym, 1),
        api.getProfile(sym).catch(() => null as CompanyProfile | null),
      ]);
      const latestQuote = quotes.length > 0 ? quotes[quotes.length - 1] : null;

      const predByTf: Record<Timeframe, Prediction | null> = {
        daily: null, weekly: null, monthly: null, yearly: null,
      };
      for (const p of preds) {
        const tf = p.timeframe as Timeframe;
        if (TIMEFRAMES.includes(tf) && (!predByTf[tf] || p.timestamp > predByTf[tf]!.timestamp)) {
          predByTf[tf] = p;
        }
      }

      return {
        symbol: sym,
        currentPrice: latestQuote?.currentPrice ?? null,
        marketCap: profile?.marketCapitalization ?? null,
        predictions: predByTf,
      } as StockSnapshot;
    });

    Promise.all(promises)
      .then(setSnapshots)
      .finally(() => setLoading(false));
  }, [selected]);

  const toggleSymbol = (sym: string) => {
    setSelected((prev) => {
      if (prev.includes(sym)) {
        const next = prev.filter((s) => s !== sym);
        return next.length >= 2 ? next : prev; // min 2
      }
      if (prev.length >= 5) return prev; // max 5
      return [...prev, sym];
    });
  };

  // Chart data: grouped bar chart for current timeframe
  const chartData = useMemo(() => {
    return snapshots.map((snap, i) => {
      const pred = snap.predictions[timeframe];
      const actual = snap.currentPrice;
      const predicted = pred?.predictedQuote ?? null;
      const diff = actual && predicted ? ((predicted - actual) / actual * 100) : null;
      return {
        symbol: snap.symbol,
        actual,
        predicted,
        diff,
        marketCap: snap.marketCap,
        color: STOCK_COLORS[i % STOCK_COLORS.length],
        recommendation: pred?.recommendation ?? 'N/A',
      };
    });
  }, [snapshots, timeframe]);

  // Market cap chart data (in billions for nicer axis values).
  // Finnhub returns market cap in millions of USD.
  const marketCapData = useMemo(() => {
    return snapshots.map((snap, i) => ({
      symbol: snap.symbol,
      marketCapB: snap.marketCap != null ? snap.marketCap / 1000 : null,
      marketCapRaw: snap.marketCap,
      color: STOCK_COLORS[i % STOCK_COLORS.length],
    }));
  }, [snapshots]);

  const totalMarketCap = useMemo(
    () => snapshots.reduce((sum, s) => sum + (s.marketCap ?? 0), 0),
    [snapshots],
  );

  // Summary table data for all timeframes
  const allTimeframeData = useMemo(() => {
    return TIMEFRAMES.map((tf) => ({
      timeframe: tf,
      stocks: snapshots.map((snap, i) => {
        const pred = snap.predictions[tf];
        return {
          symbol: snap.symbol,
          actual: snap.currentPrice,
          predicted: pred?.predictedQuote ?? null,
          recommendation: pred?.recommendation ?? 'N/A',
          date: pred?.date ?? 'N/A',
          evidence: pred?.evidenceCount ?? 0,
          color: STOCK_COLORS[i % STOCK_COLORS.length],
        };
      }),
    }));
  }, [snapshots]);

  return (
    <div>
      <h2>Compare Stocks</h2>

      {/* Symbol selector */}
      <div className="controls" style={{ flexWrap: 'wrap' }}>
        {allSymbols.map((sym) => {
          const isSelected = selected.includes(sym);
          const colorIdx = selected.indexOf(sym);
          return (
            <button
              key={sym}
              onClick={() => toggleSymbol(sym)}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: `2px solid ${isSelected ? STOCK_COLORS[colorIdx % STOCK_COLORS.length] : 'var(--border)'}`,
                background: isSelected ? `${STOCK_COLORS[colorIdx % STOCK_COLORS.length]}22` : 'var(--surface)',
                color: isSelected ? STOCK_COLORS[colorIdx % STOCK_COLORS.length] : 'var(--text-muted)',
                fontWeight: isSelected ? 700 : 400,
                cursor: isSelected && selected.length <= 2 ? 'not-allowed' : !isSelected && selected.length >= 5 ? 'not-allowed' : 'pointer',
                opacity: !isSelected && selected.length >= 5 ? 0.4 : 1,
                fontSize: 14,
              }}
            >
              {sym}
            </button>
          );
        })}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
          {selected.length}/5 selected (min 2)
        </span>
      </div>

      {/* Timeframe tabs */}
      <div className="tabs">
        {TIMEFRAMES.map((tf) => (
          <button
            key={tf}
            className={timeframe === tf ? 'active' : ''}
            onClick={() => setTimeframe(tf)}
          >
            {tf.charAt(0).toUpperCase() + tf.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">Loading comparison data...</div>
      ) : selected.length < 2 ? (
        <div className="loading">Select at least 2 stocks to compare</div>
      ) : (
        <>
          {/* Comparison bar chart */}
          <div className="chart-container">
            <h3 style={{ marginBottom: 16 }}>
              {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)} — Predicted vs Actual Price
            </h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                <XAxis dataKey="symbol" stroke="#8b8fa3" fontSize={13} fontWeight={700} />
                <YAxis stroke="#8b8fa3" fontSize={12} domain={['auto', 'auto']} />
                <Tooltip
                  contentStyle={{
                    background: '#1a1d27',
                    border: '1px solid #2a2d3a',
                    borderRadius: 8,
                    color: '#e1e4eb',
                  }}
                  formatter={(value: any, name: any) => [
                    value != null ? `$${Number(value).toFixed(2)}` : 'N/A',
                    String(name),
                  ]}
                />
                <Legend />
                <Bar dataKey="actual" name="Current Price" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} fillOpacity={0.6} />
                  ))}
                </Bar>
                <Bar dataKey="predicted" name="Predicted Price" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Comparison cards */}
          <div className="card-grid" style={{ marginBottom: 24 }}>
            {chartData.map((stock, i) => (
              <div className="card" key={stock.symbol}>
                <h3>
                  <span style={{ color: stock.color }}>{stock.symbol}</span>
                  <span className={`badge ${stock.recommendation === 'Buy' ? 'buy' : 'not-buy'}`}>
                    {stock.recommendation}
                  </span>
                </h3>
                <div className="stats-row">
                  <div className="stat">
                    <div className="value">{stock.actual != null ? `$${stock.actual.toFixed(2)}` : 'N/A'}</div>
                    <div className="label">Current Price</div>
                  </div>
                  <div className="stat">
                    <div className="value" style={{ color: stock.color }}>
                      {stock.predicted != null ? `$${stock.predicted.toFixed(2)}` : 'N/A'}
                    </div>
                    <div className="label">Predicted ({timeframe})</div>
                  </div>
                  <div className="stat">
                    <div className="value" style={{
                      color: stock.diff != null ? (stock.diff >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--text-muted)',
                    }}>
                      {stock.diff != null
                        ? `${stock.diff >= 0 ? '+' : ''}${stock.diff.toFixed(2)}%`
                        : 'N/A'}
                    </div>
                    <div className="label">Expected Change</div>
                  </div>
                  <div className="stat">
                    <div className="value">{formatMarketCap(stock.marketCap)}</div>
                    <div className="label">
                      Market Cap
                      {stock.marketCap != null && totalMarketCap > 0 && (
                        <span style={{ marginLeft: 4, color: 'var(--text-muted)' }}>
                          ({((stock.marketCap / totalMarketCap) * 100).toFixed(1)}%)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Market cap comparison chart */}
          <div className="chart-container">
            <h3 style={{ marginBottom: 16 }}>Market Capitalization (USD Billions)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={marketCapData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                <XAxis dataKey="symbol" stroke="#8b8fa3" fontSize={13} fontWeight={700} />
                <YAxis stroke="#8b8fa3" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    background: '#1a1d27',
                    border: '1px solid #2a2d3a',
                    borderRadius: 8,
                    color: '#e1e4eb',
                  }}
                  formatter={(_value: any, _name: any, item: any) => [
                    formatMarketCap(item?.payload?.marketCapRaw),
                    'Market Cap',
                  ]}
                />
                <Bar dataKey="marketCapB" name="Market Cap (B)" radius={[4, 4, 0, 0]}>
                  {marketCapData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* All timeframes overview table */}
          <div className="card">
            <h3>All Timeframes Overview</h3>
            {allTimeframeData.map(({ timeframe: tf, stocks }) => (
              <div key={tf} style={{ marginBottom: 24 }}>
                <h4 style={{
                  fontSize: 13,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'var(--text-muted)',
                  marginBottom: 8,
                  paddingBottom: 4,
                  borderBottom: '1px solid var(--border)',
                }}>
                  {tf}
                </h4>
                <table className="summary-table">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Current</th>
                      <th>Predicted</th>
                      <th>Diff</th>
                      <th>Recommendation</th>
                      <th>Date</th>
                      <th>Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stocks.map((s) => {
                      const diff = s.actual && s.predicted
                        ? ((s.predicted - s.actual) / s.actual * 100)
                        : null;
                      return (
                        <tr key={s.symbol}>
                          <td style={{ fontWeight: 700, color: s.color }}>{s.symbol}</td>
                          <td>{s.actual != null ? `$${s.actual.toFixed(2)}` : 'N/A'}</td>
                          <td>{s.predicted != null ? `$${s.predicted.toFixed(2)}` : 'N/A'}</td>
                          <td style={{
                            color: diff != null ? (diff >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--text-muted)',
                            fontWeight: 600,
                          }}>
                            {diff != null ? `${diff >= 0 ? '+' : ''}${diff.toFixed(2)}%` : '—'}
                          </td>
                          <td>
                            <span className={`badge ${s.recommendation === 'Buy' ? 'buy' : 'not-buy'}`}>
                              {s.recommendation}
                            </span>
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.date}</td>
                          <td>{s.evidence}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
